import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import TrackingService from 'api/tracking/TrackingService';
import { AddressHydrationStatus } from 'api/tracking/models/AddressHydrationStatus';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { Reducers } from 'store/types';

// God mode (Platform Admin): abre o URL e força a hidratação das moradas em falta nas viagens.
const RunAddressHydrationScreen: React.FC = () => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const [status, setStatus] = useState<AddressHydrationStatus | null>(null);
    const pollingRef = useRef<NodeJS.Timeout | null>(null);
    const startedRef = useRef(false);

    const start = async () => {
        if (!UsersService.isPlatformAdmin(userProfile?.roles) || startedRef.current) return;
        startedRef.current = true;

        try {
            await TrackingService.runAddressHydration();
            startPolling();
        } catch (error: any) {
            if (error?.response?.status === 409) {
                // Já está a correr — apenas acompanhar.
                startPolling();
            } else {
                addToast(t('common.messages.error_load_info') as string, { appearance: 'error' });
            }
        }
    };

    const startPolling = () => {
        if (pollingRef.current) return;
        pollStatus();
        pollingRef.current = setInterval(pollStatus, 3000);
    };

    const pollStatus = async () => {
        try {
            const result = await TrackingService.getAddressHydrationStatus();
            setStatus(result);

            if (!result.isRunning) {
                stopPolling();
                if (result.lastError) {
                    addToast(result.lastError, { appearance: 'error' });
                } else {
                    addToast(t('common.messages.record_save_success') as string, { appearance: 'success' });
                }
            }
        } catch {
            stopPolling();
            addToast(t('common.messages.error_load_info') as string, { appearance: 'error' });
        }
    };

    const stopPolling = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };

    useEffect(() => {
        start();
        return () => stopPolling();
    }, []);

    const progressPercent = status && status.lastTotal > 0
        ? Math.round((status.lastProcessed / status.lastTotal) * 100)
        : 0;

    return (
        <div style={{ padding: '40px', textAlign: 'center' }}>
            <h2>Hidratação de moradas</h2>
            {status?.isRunning && (
                <>
                    <div style={{ margin: '20px auto', maxWidth: '400px', backgroundColor: '#e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                        <div style={{
                            width: `${progressPercent}%`,
                            height: '30px',
                            backgroundColor: '#02c7c7',
                            transition: 'width 0.5s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            minWidth: progressPercent > 0 ? '40px' : '0',
                        }}>
                            {progressPercent}%
                        </div>
                    </div>
                    <p>{status.lastProcessed} / {status.lastTotal} viagens &middot; {status.lastGeocoded} moradas obtidas</p>
                </>
            )}
            {status && !status.isRunning && !status.lastError && status.lastFinishedUtc && (
                <>
                    <h3>{t('common.messages.record_save_success') as string}</h3>
                    <p>{status.lastProcessed} viagens processadas &middot; {status.lastGeocoded} moradas obtidas</p>
                </>
            )}
            {status && !status.isRunning && status.lastError && (
                <h3 style={{ color: '#e04f5f' }}>{status.lastError}</h3>
            )}
        </div>
    );
};

export default RunAddressHydrationScreen;
